import { type KeyboardEvent, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cx } from "./cx";
import { Icon, type IconName } from "./Icon";
import { placeBeside } from "./place";

export interface MenuItem {
  label: string;
  /** A second line under the label: "PDF or document". */
  sub?: string;
  icon?: IconName;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
}

/**
 * A row's actions behind one button (`.menu`): a menu button, a menu of items, arrow keys through them,
 * Esc or a click outside to close, and focus back on the button after.
 */
export function Menu({
  items,
  label,
  icon = "more",
  align = "right",
  buttonClassName,
  menuClassName,
}: {
  /** The items; "sep" draws a line between groups. */
  items: ReadonlyArray<MenuItem | "sep">;
  /** What the button opens, read aloud: "Actions for Priya Raman". */
  label: string;
  icon?: IconName;
  align?: "left" | "right";
  buttonClassName?: string;
  menuClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const [spot, setSpot] = useState<{ top: number; left: number } | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const id = useId();

  const close = useCallback((refocus: boolean) => {
    setOpen(false);
    setSpot(null);
    if (refocus) button.current?.focus();
  }, []);

  useLayoutEffect(() => {
    if (!open || !button.current || !menu.current) return;
    setSpot(
      placeBeside(button.current.getBoundingClientRect(), { width: menu.current.offsetWidth, height: menu.current.offsetHeight }, align),
    );
    menu.current.querySelector<HTMLButtonElement>("button:not([disabled])")?.focus({ preventScroll: true });
  }, [open, align]);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!menu.current?.contains(e.target as Node) && !button.current?.contains(e.target as Node)) close(false);
    };
    const gone = () => close(false);
    document.addEventListener("mousedown", away);
    addEventListener("resize", gone);
    return () => {
      document.removeEventListener("mousedown", away);
      removeEventListener("resize", gone);
    };
  }, [open, close]);

  const keys = (e: KeyboardEvent<HTMLDivElement>) => {
    const all = [...(menu.current?.querySelectorAll<HTMLButtonElement>("button:not([disabled])") ?? [])];
    const at = all.indexOf(document.activeElement as HTMLButtonElement);
    const to = (i: number) => {
      e.preventDefault();
      all[(i + all.length) % all.length]?.focus();
    };
    if (e.key === "ArrowDown") to(at + 1);
    else if (e.key === "ArrowUp") to(at - 1);
    else if (e.key === "Home") to(0);
    else if (e.key === "End") to(all.length - 1);
    else if (e.key === "Escape") {
      e.preventDefault();
      close(true);
    } else if (e.key === "Tab") close(false);
  };

  return (
    <>
      <button
        ref={button}
        type="button"
        className={cx("ibtn", buttonClassName)}
        aria-label={label}
        title={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => (open ? close(false) : setOpen(true))}
      >
        <Icon name={icon} />
      </button>
      {open
        ? createPortal(
            <div
              ref={menu}
              id={id}
              className={cx("menu", menuClassName)}
              role="menu"
              aria-label={label}
              style={spot ?? { visibility: "hidden", top: 0, left: 0 }}
              onKeyDown={keys}
            >
              {items.map((item, i) =>
                item === "sep" ? (
                  <div key={`sep-${i}`} className="sep" role="separator" />
                ) : (
                  <button
                    key={item.label}
                    type="button"
                    role="menuitem"
                    className={cx(item.danger && "danger", item.sub !== undefined && "cm-i")}
                    disabled={item.disabled}
                    onClick={() => {
                      close(true);
                      item.onSelect();
                    }}
                  >
                    {item.icon ? <Icon name={item.icon} /> : null}
                    {item.sub !== undefined ? (
                      <span className="sp">
                        <b>{item.label}</b>
                        {item.sub ? <span>{item.sub}</span> : null}
                      </span>
                    ) : (
                      item.label
                    )}
                  </button>
                ),
              )}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
